<template>
  <div>
    <div v-for="(item, index) in results" :key="index" class="content-wrapper">
      <z-icon ref="check-icon" :color="item.isValid ? color || 'green' : color">
        {{ item.isValid ? checkedIcon : uncheckedIcon }}
      </z-icon>
      <p :style="{color}" class="body-text">{{ item.title }}</p>
    </div>
  </div>
</template>

<script>
/**
 * Componente que indica a força de uma senha. O componente
 * exibe quais são os itens de força de uma senha que o valor
 * está respeitando. O componente permite também que sejam adicionadas
 * tipos de validações customizadas através do extraRules.
 *
 * @displayName Password Strength
 */
export default {
  name: 'ZPasswordStrength',
  props: {
    /**
     * Valor a ser avaliado pelas verificações
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * Cor do texto e ícones da validação da senha
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Define o número minimo de caracteres
     */
    minLength: {
      type: [Number, String],
      required: false,
    },
    /**
     * Aplica a verificação para conter pelo menos um caractere maiúsculo
     */
    withUpperCase: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Aplica a verificação para conter pelo menos um caractere minúsculo
     */
    withLowerCase: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Aplica a verificação para conter pelo menos um caractere numérico
     */
    withNumber: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Aplica a verificação para conter pelo menos um caractere especial
     */
    withSpecialCharacter: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Array de objetos que aplica validações customizadas definidas pelo usuário
     */
    extraRules: {
      type: Array,
      required: false,
    },
    /**
     * Define o ícone que será exibido quando a senha for válida
     */
    checkedIcon: {
      type: String,
      default: '$check_in_a_circle',
    },
    /**
     * Define o ícone que será exibido quando a senha for inválida
     */
    uncheckedIcon: {
      type: String,
      default: '$radio_button_unchecked_rounded',
    },
  },

  computed: {
    validators() {
      const validators = []

      if (this.minLength) {
        validators.push({
          title: this.$t('more_than_n_characters', {
            number: this.minLength,
          }),
          validation: (input) => input.length > this.minLength,
        })
      }

      if (this.withUpperCase) {
        validators.push({
          title: this.$t('capital_letter'),
          validation: (input) => /.*[A-Z].*/.test(input),
        })
      }

      if (this.withLowerCase) {
        validators.push({
          title: this.$t('lower_case'),
          validation: (input) => /.*[a-z].*/.test(input),
        })
      }

      if (this.withNumber) {
        validators.push({
          title: this.$t('number'),
          validation: (input) => /.*\d.*/.test(input),
        })
      }

      if (this.withSpecialCharacter) {
        validators.push({
          title: this.$t('special_character'),
          validation: (input) => /[-+_!@#$%^&*.,?]/.test(input),
        })
      }

      if (this.extraRules) {
        validators.push(...this.extraRules)
      }

      return validators
    },

    results() {
      return this.validators.map((validator) => ({
        title: validator.title,
        isValid: validator.validation(this.value),
      }))
    },
  },

  watch: {
    results(newValue) {
      const number = newValue.reduce((acc, obj) => {
        if (obj.isValid) {
          acc += 1
        }

        return acc
      }, 0)
      /**
       * Evento emitido a cada mudança no número de validações
       *
       * @property {Number} number - Número de validações válidas
       */
      this.$emit('change', number)
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.results) {
        const numberOfValidations = this.results.reduce((acc, obj) => {
          if (obj.isValid) {
            acc += 1
          }

          return acc
        }, 0)

        this.$emit('change', numberOfValidations)
      }
    })
  },

  i18n: {
    messages: {
      pt: {
        more_than_n_characters: 'Mais de {number} caracteres',
        capital_letter: 'Letra maiúscula',
        lower_case: 'Letra minúscula',
        number: 'Número',
        special_character: 'Caractere especial',
      },
      en: {
        more_than_n_characters: 'More than {number} characters',
        capital_letter: 'Capital letter',
        lower_case: 'Lower case',
        number: 'Number',
        special_character: 'Special character',
      },
      es: {
        more_than_n_characters: 'Más de {number} caracteres',
        capital_letter: 'Letra mayúscula',
        lower_case: 'Letra minúscula',
        number: 'Número',
        special_character: 'Caractere especial',
      },
      fr: {
        more_than_n_characters: 'Plus de {number} caractères',
        capital_letter: 'Lettre majuscule',
        lower_case: 'Minuscule',
        number: 'Nombre',
        special_character: 'Caractère spécial',
      },
      tr: {
        more_than_n_characters: '{number} karakterden fazla',
        capital_letter: 'Büyük harf',
        lower_case: 'Küçük harf',
        number: 'Sayı',
        special_character: 'Özel karakter',
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 5px;
  justify-content: center;
  margin-bottom: 6px;
  p {
    margin: 0;
  }
}
</style>
